import { jsPDF } from "jspdf";

/**
 * Generate a PDF of shipment labels. If autoPackages is true and package_total > 1,
 * the PDF will contain one page per package numbered 1/N, 2/N, ... N/N.
 * Otherwise a single page using package_number.
 *
 * Each page is sized exactly to the chosen label dimensions in millimeters.
 */
export function generateLabelPDF(shipment) {
  const ls = shipment.label_size || { width_mm: 152, height_mm: 102 };
  const w = Number(ls.width_mm) || 152;
  const h = Number(ls.height_mm) || 102;

  // Use landscape if width > height
  const orientation = w >= h ? "landscape" : "portrait";
  const pageW = orientation === "landscape" ? Math.max(w, h) : Math.min(w, h);
  const pageH = orientation === "landscape" ? Math.min(w, h) : Math.max(w, h);

  const doc = new jsPDF({
    orientation,
    unit: "mm",
    format: [pageW, pageH],
  });

  const isAuto = shipment.package_mode === "auto";
  const isManual = shipment.package_mode === "manual";
  const total = Math.max(1, Number(shipment.package_total) || 1);

  const drawLabel = (pageNum) => {
    const pad = 6;
    const innerW = pageW - 2 * pad;
    // Outer dashed border
    doc.setLineDashPattern([1.5, 1.5], 0);
    doc.setDrawColor(120);
    doc.setLineWidth(0.4);
    doc.rect(pad, pad, innerW, pageH - 2 * pad);
    doc.setLineDashPattern([], 0);

    // Top barcode bar
    drawBarcodeBar(doc, pad + 2, pad + 3, innerW - 4, 6);

    // Header label
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(60);
    doc.text("SHIPMENT CODE", pad + 4, pad + 16);

    // Mode + DDMM right header
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    const right = `${shipment.shipment_mode || ""} · ${getDDMM(shipment.created_at)}`;
    doc.text(right, pageW - pad - 4, pad + 16, { align: "right" });

    // Generated code (big)
    const codeFontSize = computeCodeFontSize(doc, shipment.generated_code || "", innerW - 8);
    doc.setFont("courier", "bold");
    doc.setFontSize(codeFontSize);
    doc.setTextColor(20);
    const codeY = pad + 16 + codeFontSize * 0.45 + 6;
    doc.text(shipment.generated_code || "", pageW / 2, codeY, { align: "center" });

    let nextY = codeY;

    if (isAuto) {
      // Auto mode: big page-number fraction
      const pkgText = `${pageNum}/${total}`;
      const pkgFontSize = computeCodeFontSize(doc, pkgText, innerW - 8);
      doc.setFont("courier", "bold");
      doc.setFontSize(pkgFontSize);
      const pkgY = codeY + codeFontSize * 0.5 + pkgFontSize * 0.45 + 6;
      doc.text(pkgText, pageW / 2, pkgY, { align: "center" });
      nextY = pkgY;
    } else if (isManual) {
      // Manual mode: "Pkg No :" rendered at 60% of the code font size (40% smaller).
      const pkgText = "Pkg No :";
      const pkgFontSize = codeFontSize * 0.6;
      doc.setFont("courier", "bold");
      doc.setFontSize(pkgFontSize);
      const pkgY = codeY + codeFontSize * 0.5 + pkgFontSize * 0.45 + 6;
      doc.text(pkgText, pageW / 2, pkgY, { align: "center" });
      nextY = pkgY;
    }

    // Bottom barcode bar
    drawBarcodeBar(doc, pad + 2, pageH - pad - 9, innerW - 4, 6);

    // EN NO at bottom-left, small
    if (shipment.en_no) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(6.5);
      doc.setTextColor(80);
      doc.text(`EN NO: ${shipment.en_no}`, pad + 4, pageH - pad - 11);
    }
  };

  if (isAuto && total > 1) {
    for (let i = 1; i <= total; i++) {
      if (i > 1) doc.addPage([pageW, pageH], orientation);
      drawLabel(i);
    }
  } else {
    drawLabel(1);
  }

  return doc;
}

function computeCodeFontSize(doc, text, maxWidthMm) {
  if (!text) return 16;
  // Try sizes from 36 down to 10
  for (let size = 36; size >= 10; size -= 1) {
    doc.setFont("courier", "bold");
    doc.setFontSize(size);
    const w = doc.getTextWidth(text);
    if (w <= maxWidthMm) return size;
  }
  return 10;
}

function drawBarcodeBar(doc, x, y, w, h) {
  // Draw vertical lines of varying widths to mimic a barcode
  doc.setDrawColor(20);
  let cursor = x;
  const widths = [0.5, 0.8, 1.2, 0.4, 0.6, 1.0, 0.5, 0.8];
  let i = 0;
  while (cursor < x + w) {
    const bw = widths[i % widths.length];
    doc.setLineWidth(bw);
    doc.line(cursor, y, cursor, y + h);
    cursor += bw + 0.6;
    i += 1;
  }
}

function getDDMM(iso) {
  try {
    const d = iso ? new Date(iso) : new Date();
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `${dd}${mm}`;
  } catch {
    return "";
  }
}

export function downloadLabelPDF(shipment, filename) {
  const doc = generateLabelPDF(shipment);
  doc.save(filename || `${shipment.generated_code || "label"}.pdf`);
}

export function printLabelPDF(shipment) {
  const doc = generateLabelPDF(shipment);
  // Open in a new window and trigger print
  const blob = doc.output("blob");
  const url = URL.createObjectURL(blob);
  const w = window.open(url);
  if (w) {
    w.addEventListener("load", () => {
      try {
        w.focus();
        w.print();
      } catch (err) {
        // Some browsers block programmatic print; user can still print manually.
        console.warn("Auto-print failed; user can trigger print manually.", err);
      }
    });
  }
}
